import * as React from 'react'

import { Button } from '@/components/ui/button'

import { ConversationType } from '@/types/chat.type'

interface DeleteConversationDialogProps {
  isOpen: boolean
  isDeleting: boolean
  conversationType?: ConversationType
  onClose: () => void
  onConfirm: (permanent: boolean) => void
}

export const DeleteConversationDialog: React.FC<DeleteConversationDialogProps> = ({
  isOpen,
  isDeleting,
  conversationType,
  onClose,
  onConfirm,
}) => {
  const [isPermanent, setIsPermanent] = React.useState(false)

  // Only show permanent delete option for non-direct conversations
  const showDeleteOptions = conversationType && conversationType !== ConversationType.DIRECT

  const handleClose = () => {
    setIsPermanent(false) // Reset to default
    onClose()
  }

  const handleConfirm = () => {
    onConfirm(isPermanent)
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />
      <div className="relative w-full max-w-md bg-white rounded-lg shadow-xl">
        <div className="p-6">
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Delete Conversation</h3>
          </div>
          <div className="space-y-4">
            {showDeleteOptions ? (
              <>
                <p className="text-sm text-gray-800">Choose how you want to delete this conversation:</p>
                <div className="space-y-3">
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="deleteType"
                      checked={!isPermanent}
                      onChange={() => setIsPermanent(false)}
                      className="mt-1 w-4 h-4 text-blue-600"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-gray-900">Remove for me</div>
                      <div className="text-xs text-gray-600">
                        Hide this conversation from your view. Other participants will still see it.
                      </div>
                    </div>
                  </label>
                  <label className="flex items-start space-x-3 cursor-pointer">
                    <input
                      type="radio"
                      name="deleteType"
                      checked={isPermanent}
                      onChange={() => setIsPermanent(true)}
                      className="mt-1 w-4 h-4 text-red-600"
                    />
                    <div className="flex-1">
                      <div className="text-sm font-medium text-red-600">Delete permanently</div>
                      <div className="text-xs text-gray-600">
                        Permanently delete this conversation for everyone. This action cannot be undone.
                      </div>
                    </div>
                  </label>
                </div>
              </>
            ) : (
              <p className="text-sm text-gray-800">
                Are you sure you want to remove this conversation from your chat list? This will only hide it from your
                view and won't affect other participants.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleConfirm} className="min-w-[80px]">
                {isDeleting ? (
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                    {isPermanent && showDeleteOptions ? 'Deleting...' : 'Removing...'}
                  </div>
                ) : isPermanent && showDeleteOptions ? (
                  'Delete'
                ) : (
                  'Remove'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
